import { Box, Card, CardContent, Typography, Divider, IconButton, Collapse, Alert, CircularProgress } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'
import { useEffect, useRef, useState } from 'react'
import CustomerPlot from './plotting/CustomerPlot'

const MAX_HISTORY_MS = 1 * 60 * 60 * 1000 // 1 hour

function RealtimeTab({ history, availableFields, status }) {
  const metrics = [
    { name: 'alkalinity', label: 'Total Alkalinity', unit: 'ppm', decimals: 1, color: '#2196f3' },
    { name: 'ph', label: 'pH', unit: '', decimals: 2, color: '#4caf50' },
    { name: 'temperature', label: 'Temperature', unit: '°C', decimals: 1, color: '#ff9800' },
    { name: 'conductivity', label: 'Conductivity', unit: 'mS/cm', decimals: 2, color: '#9c27b0' }
  ]

  const visibleMetrics = metrics.filter(metric => availableFields[metric.name])

  if (status === 'connecting' || status === 'disconnected') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress sx={{ mr: 2 }} />
      </Box>
    )
  }

  if (status === 'error') {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Connection Error
          </Typography>
          <Typography variant="body2">
            Failed to connect to real-time data stream. Please refresh the page to try again.
          </Typography>
        </Alert>
      </Box>
    )
  }

  const hasAnyData = visibleMetrics.some(metric => history[metric.name]?.length > 0)

  if (!hasAnyData) {
    const timeRangeMinutes = MAX_HISTORY_MS / (1000 * 60)
    const timeRangeDisplay = timeRangeMinutes >= 60 
      ? `${timeRangeMinutes / 60} hour${timeRangeMinutes / 60 > 1 ? 's' : ''}`
      : `${timeRangeMinutes} minutes`

    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            No Real-Time Data Available
          </Typography>
          <Typography variant="body2">
            No measurements have been published within the last {timeRangeDisplay}. 
            Real-time data will appear here once new measurements are received.
          </Typography>
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="div" fontWeight="bold" sx={{ mb: 4 }}>
        Real-Time Trends
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
        {visibleMetrics.map(metric => (
          <MetricWidget
            key={metric.name}
            metric={metric}
            history={history[metric.name]}
          />
        ))}
      </Box>
    </Box>
  )
}

function MetricWidget({ metric, history }) {
  const [expanded, setExpanded] = useState(true)
  const [isHovered, setIsHovered] = useState(false)
  const latestData = history && history.length > 0 ? history[history.length - 1] : null

  const formatValue = (value) => {
    if (value == null) return '--'
    return Number(value).toFixed(metric.decimals)
  }

  return (
    <Card 
      sx={{ 
        width: '100%',
        transition: 'all 0.3s ease-in-out',
        border: !expanded && isHovered ? `2px solid ${metric.color}` : '2px solid transparent',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Collapsed View - Horizontal Row */}
        <Collapse in={!expanded} timeout={500}>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 3,
              cursor: 'pointer',
              opacity: !expanded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
            onClick={() => setExpanded(true)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
              <Box sx={{ borderLeft: `4px solid ${metric.color}`, pl: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
                  {metric.label}
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: metric.color }}>
                  {formatValue(latestData?.value)}
                  {metric.unit && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      {metric.unit}
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {metric.label} Trend
              </Typography>
            </Box>
            <IconButton size="small">
              <ExpandMore />
            </IconButton>
          </Box>
        </Collapse>

        {/* Expanded View - Full Card and Graph */}
        <Collapse in={expanded} timeout={500}>
          <Box
            sx={{
              opacity: expanded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <Box 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center"
              sx={{ cursor: 'pointer', mb: 3 }}
              onClick={() => setExpanded(false)}
            >
              <Typography variant="h6" component="div" fontWeight="bold" sx={{ color: metric.color }}>
                {metric.label} Trend
              </Typography>
              <IconButton size="small">
                <ExpandLess />
              </IconButton>
            </Box>

            <Box 
              sx={{ 
                display: 'flex', 
                gap: 3, 
                width: '100%',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'stretch', md: 'flex-start' }
              }}
            >
              <Box sx={{ width: { xs: '100%', md: '300px' }, flexShrink: 0 }}>
                <MetricCardContent
                  metric={metric}
                  history={history}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <CustomerPlot
                  data={history}
                  dataRevision={history?.length ?? 0}
                  label={metric.label}
                  unit={metric.unit}
                  color={metric.color}
                  decimals={metric.decimals}
                  liveWindowMs={MAX_HISTORY_MS}
                />
              </Box>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

function MetricCardContent({ metric, history }) {
  const latestData = history && history.length > 0 ? history[history.length - 1] : null
  const valueRef = useRef(null)

  useEffect(() => {
    if (latestData && valueRef.current) {
      valueRef.current.style.transform = 'scale(1.05)'
      setTimeout(() => {
        if (valueRef.current) {
          valueRef.current.style.transform = 'scale(1)'
        }
      }, 200)
    }
  }, [latestData])

  const formatValue = (value) => {
    if (value == null) return '--'
    return Number(value).toFixed(metric.decimals)
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return 'No data'
    const date = new Date(timestamp)
    return date.toLocaleTimeString()
  }

  const stats = history && history.length > 0 ? {
    min: Math.min(...history.map(d => d.value).filter(v => v != null)),
    max: Math.max(...history.map(d => d.value).filter(v => v != null)),
    avg: history.reduce((sum, d) => sum + (d.value || 0), 0) / history.filter(d => d.value != null).length
  } : null

  return (
    <Box 
      sx={{ 
        height: '100%',
        border: `2px solid ${metric.color}`,
        borderTop: `4px solid ${metric.color}`,
        borderRadius: 1,
        p: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {metric.label}
      </Typography>
      
      <Box
        ref={valueRef}
        sx={{
          transition: 'transform 0.2s',
          mb: 2
        }}
      >
        <Typography 
          variant="h3" 
          component="div" 
          fontWeight="bold"
          sx={{ color: metric.color }}
        >
          {formatValue(latestData?.value)}
          {metric.unit && (
            <Typography 
              component="span" 
              variant="h6" 
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              {metric.unit}
            </Typography>
          )}
        </Typography>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Updated: {formatTime(latestData?.timestamp)}
      </Typography>

      {stats && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Min
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {stats.min.toFixed(metric.decimals)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Avg
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {stats.avg.toFixed(metric.decimals)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Max
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {stats.max.toFixed(metric.decimals)}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default RealtimeTab
